import React, { useState } from 'react';

const EventSimulator = () => {
  const [broadcastUrl, setBroadcastUrl] = useState('https://realtime-data.gotrasoft.com/api/broadcast');
  const [ackUrl, setAckUrl] = useState('https://realtime-data.gotrasoft.com/api/acknowledge');
  const [roomId, setRoomId] = useState('kuber_atv_room');
  const [eventType, setEventType] = useState('booking_created');
  const [eventIdToAck, setEventIdToAck] = useState('');
  const [jsonPayload, setJsonPayload] = useState(JSON.stringify({
    id: 'test-123',
    code: 'TEST001',
    guest_name: 'Test User',
    total_pax: 2,
    reservation_date: new Date().toISOString().split('T')[0],
    status: 'booked',
    total_price: 500000
  }, null, 2));
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('send-event');
  const [isLoading, setIsLoading] = useState(false);

  const sendEvent = async () => {
    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      let payloadToSend;
      try {
        payloadToSend = JSON.parse(jsonPayload);
      } catch (parseError) {
        throw new Error('JSON Payload tidak valid: ' + parseError.message);
      }

      const dataToPost = {
        room_id: roomId,
        event_type: eventType,
        json_payload: payloadToSend
      };

      const response = await fetch(broadcastUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(dataToPost),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Respons server tidak sukses.');
      }

      setResponse({
        status: response.status,
        data: result
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const acknowledgeEvent = async () => {
    setIsLoading(true);
    setError(null);
    setResponse(null);

    if (!eventIdToAck.trim()) {
      setError('Event ID tidak boleh kosong.');
      setIsLoading(false);
      return;
    }

    try {
      const dataToPost = {
        event_id: parseInt(eventIdToAck)
      };

      const response = await fetch(ackUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(dataToPost),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Respons server tidak sukses.');
      }

      setResponse({
        status: response.status,
        data: result
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const generateCurlCommand = (type) => {
    if (type === 'send') {
      const data = JSON.stringify({
        room_id: roomId,
        event_type: eventType,
        json_payload: JSON.parse(jsonPayload)
      });
      return `curl -X POST "${broadcastUrl}" \\\n     -H "Content-Type: application/json" \\\n     -d '${data}'`;
    } else if (type === 'ack') {
      const data = JSON.stringify({
        event_id: parseInt(eventIdToAck || 0)
      });
      return `curl -X POST "${ackUrl}" \\\n     -H "Content-Type: application/json" \\\n     -d '${data}'`;
    }
    return '';
  };

  const generateLaravelCode = (type) => {
    if (type === 'send') {
      return `use Illuminate\\Support\\Facades\\Http;

$response = Http::post('${broadcastUrl}', [
    'room_id' => '${roomId}',
    'event_type' => '${eventType}',
    'json_payload' => ${JSON.stringify(JSON.parse(jsonPayload), null, 4).replace(/\n/g, '\n    ')}
]);`;
    } else if (type === 'ack') {
      return `use Illuminate\\Support\\Facades\\Http;

$response = Http::post('${ackUrl}', [
    'event_id' => ${parseInt(eventIdToAck || 0)},
]);`;
    }
    return '';
  };

  return (
    <div className="event-simulator max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Event & Acknowledgment Simulator</h1>
        <p className="text-gray-500 mt-2">
          Gunakan alat ini untuk mengirim event atau mensimulasikan acknowledgment ke server realtime Anda.
        </p>
      </div>

      {/* Tab Navigation */}
      <nav className="flex justify-center p-1.5 bg-gray-200 rounded-xl mb-6 max-w-md mx-auto">
        <button
          onClick={() => setActiveTab('send-event')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-colors ${
            activeTab === 'send-event'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <svg className="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.949a.75.75 0 00.95.826L11.25 6.49l-6.772-3.252z" />
            <path d="M12.94 6.49a.75.75 0 010 1.02l-3.252 6.772a.75.75 0 01-1.02 0l-6.772-3.252a.75.75 0 010-1.02l3.252-6.772a.75.75 0 011.02 0l6.772 3.252z" />
          </svg>
          Kirim Event Baru
        </button>
        <button
          onClick={() => setActiveTab('acknowledge-event')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-colors ${
            activeTab === 'acknowledge-event'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <svg className="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.403 3.94a.75.75 0 01.07 1.05l-8.25 9.5a.75.75 0 01-1.13.075L3.055 9.44a.75.75 0 011.07-1.05l2.47 2.235 7.63-8.775a.75.75 0 011.05-.07z" clipRule="evenodd" />
          </svg>
          Acknowledge Event
        </button>
      </nav>

      {/* Send Event Tab */}
      {activeTab === 'send-event' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <form onSubmit={(e) => { e.preventDefault(); sendEvent(); }} className="space-y-5">
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">
                  URL Endpoint Broadcast:
                </label>
                <input
                  type="url"
                  value={broadcastUrl}
                  onChange={(e) => setBroadcastUrl(e.target.value)}
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">Room ID:</label>
                <input
                  type="text"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">Event Type:</label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="booking_created">booking_created</option>
                  <option value="booking_updated">booking_updated</option>
                  <option value="booking_status_changed">booking_status_changed</option>
                  <option value="user_registered">user_registered</option>
                  <option value="transaction_completed">transaction_completed</option>
                </select>
              </div>
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">JSON Payload:</label>
                <textarea
                  value={jsonPayload}
                  onChange={(e) => setJsonPayload(e.target.value)}
                  rows="7"
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 rounded-lg text-sm transition-colors"
                >
                  {isLoading ? 'Mengirim...' : 'Kirim Event'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-semibold text-white mb-4">Implementasi Kode</h3>
              <nav className="flex bg-gray-700 rounded-lg p-1 mb-4">
                <button className="flex-1 py-2 px-3 rounded-md text-sm font-medium text-white bg-gray-600">
                  cURL
                </button>
                <button className="flex-1 py-2 px-3 rounded-md text-sm font-medium text-gray-300">
                  Laravel
                </button>
              </nav>
              <div className="flex-grow">
                <div className="relative h-full">
                  <pre className="bg-gray-900 text-gray-200 p-4 rounded-lg h-full overflow-auto text-xs">
                    <code>{generateCurlCommand('send')}</code>
                  </pre>
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand('send'))}
                    className="absolute top-2 right-2 bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledge Event Tab */}
      {activeTab === 'acknowledge-event' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <form onSubmit={(e) => { e.preventDefault(); acknowledgeEvent(); }} className="space-y-5">
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">
                  URL Endpoint Acknowledge:
                </label>
                <input
                  type="url"
                  value={ackUrl}
                  onChange={(e) => setAckUrl(e.target.value)}
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">Event ID to Acknowledge:</label>
                <input
                  type="number"
                  value={eventIdToAck}
                  onChange={(e) => setEventIdToAck(e.target.value)}
                  required
                  placeholder="Contoh: 123"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 rounded-lg text-sm transition-colors"
                >
                  {isLoading ? 'Mengirim...' : 'Kirim Acknowledgment'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-semibold text-white mb-4">Implementasi Kode</h3>
              <nav className="flex bg-gray-700 rounded-lg p-1 mb-4">
                <button className="flex-1 py-2 px-3 rounded-md text-sm font-medium text-white bg-gray-600">
                  cURL
                </button>
                <button className="flex-1 py-2 px-3 rounded-md text-sm font-medium text-gray-300">
                  Laravel
                </button>
              </nav>
              <div className="flex-grow">
                <div className="relative h-full">
                  <pre className="bg-gray-900 text-gray-200 p-4 rounded-lg h-full overflow-auto text-xs">
                    <code>{generateCurlCommand('ack')}</code>
                  </pre>
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand('ack'))}
                    className="absolute top-2 right-2 bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response/Error Display */}
      {(response || error) && (
        <div className="mt-8 max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-gray-700 mb-3">Respons Server:</h2>
          {error ? (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-md" role="alert">
              <p className="font-bold">Error Terjadi</p>
              <p>{error}</p>
            </div>
          ) : response ? (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-800 p-4 rounded-md">
              <p className="mb-1">
                HTTP Status: <span className="font-semibold">{response.status}</span>
              </p>
              <pre className="bg-gray-800 text-white p-3 rounded-md text-xs overflow-auto">
                {JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default EventSimulator;